import { useMutation, useQuery } from "@tanstack/react-query";
import { ATTRAPPE, hole, holeDatei } from "./klient";
import { archivvermerkAus, type Archivvermerk } from "./archivvermerk";
import { papierOeffnen } from "./papieroeffnen";

/* ══════════════════════════════════════════════════════════════════
   Die Belegsuche — Formen, Weg und Haken (18.09.2026).

   Luca: *„Eine Belegsuche, bei der wir live über die API alle Belege
   abfragen … Lieferscheine, Auftragsbestätigung, Angebote, Rechnungen.
   Dann den Kunden ein oder das Datum, und dann findest du alle Belege."*

   **Eine eigene Datei, wie beim Nachschlagen.** Der Schirm hat seinen
   eigenen Weg (`GET /belege`), und die globale Suche im Kopf fragt
   denselben. Angeglichen wird hier und nur hier: In der Maske steht kein
   Feldname aus der Antwort.

   **Alle Papiere in einer Liste** (06.10.2026): Angebot, AB, Übergabe-
   und Rücknahmeprotokoll aus Machine Office (je Papier die jüngste
   Fassung, die älteren daran), Rechnungen und Gutschriften live aus
   Lexware Office (`apps/api/src/dienste/belegsuche.ts`). Der Server hält
   die Lexware-Liste eine Minute lang; „Aktualisieren" holt sie sofort neu.
   ══════════════════════════════════════════════════════════════════ */

type Roh = Record<string, unknown>;

const txt = (wert: unknown): string =>
  wert === null || wert === undefined ? "" : typeof wert === "string" ? wert : String(wert);
const zahlOderNull = (wert: unknown): number | null => {
  if (wert === null || wert === undefined || wert === "") return null;
  const n = typeof wert === "number" ? wert : Number(wert);
  return Number.isFinite(n) ? n : null;
};

/**
 * Die Arten der Belegliste — in der Reihenfolge des Weges. Seit dem
 * 06.10.2026 sechs: Angebot, Auftragsbestätigung, Übergabeprotokoll (der
 * Lieferschein) und Rücknahmeprotokoll aus Machine Office, Rechnung und
 * Gutschrift aus Lexware Office.
 */
export const BELEGARTEN = [
  "angebot", "auftragsbestaetigung", "uebergabeprotokoll", "ruecknahmeprotokoll", "rechnung", "gutschrift",
] as const;
export type Belegart = (typeof BELEGARTEN)[number];

/** Der Zustand über die Arten hinweg — dieselben Wörter wie in `dienste/belegsuche.ts`. */
export const BELEGZUSTAENDE = [
  "offen", "ueberfaellig", "bezahlt", "ausgestellt", "angenommen", "abgelehnt", "entwurf", "storniert", "ersetzt",
  "sonstig",
] as const;
export type Belegzustand = (typeof BELEGZUSTAENDE)[number];

const istArt = (wert: unknown): wert is Belegart =>
  typeof wert === "string" && (BELEGARTEN as readonly string[]).includes(wert);
const alsZustand = (wert: unknown): Belegzustand =>
  typeof wert === "string" && (BELEGZUSTAENDE as readonly string[]).includes(wert)
    ? (wert as Belegzustand)
    : "sonstig";

export type Beleg = {
  id: string;
  art: Belegart;
  /** `RE0012` — die Nummer, die der Kunde am Telefon nennt. Ein Entwurf hat manchmal keine. */
  nummer: string;
  zustand: Belegzustand;
  /** Der Zustand drüben, unverändert (`paidoff`, `voided` …). */
  status: string;
  /** Belegdatum als ISO-Tag. */
  datum: string;
  faellig: string;
  /** Der Name auf dem Beleg. */
  name: string;
  /** Brutto. `null` beim Lieferschein — er fordert kein Geld. */
  summe: number | null;
  offen: number | null;
  archiviert: boolean;
  /** Der Deeplink nach Lexware Office. */
  adresse: string;
  /** Der Kontakt des Empfängers in Lexware Office — der Weg, wenn er bei uns kein Kunde ist. */
  kontaktAdresse: string;
  /** Der Kunde bei uns — über Kontakt, Vorgang oder Namen (`apps/api/src/dienste/belegsuche.ts`). */
  kundeNr: string;
  kundeName: string;
  kundeOrt: string;
  /** Der Vorgang bei uns, wenn der Beleg von hier hinausging. */
  vorgangNr: string;
  vorgangStatus: string;
  /** Seit 0200 entstehen Angebote in Machine Office — sie liegen nur hier, nicht in Lexware Office. */
  ausMachineOffice: boolean;
  /** Machine Office: die jüngste Fassung (`null` bei Lexware oder vor dem Festschreiben). */
  fassung: number | null;
  /** Machine Office: die älteren Fassungen, die jüngste zuerst. */
  fassungen: Belegfassung[];
  /** Machine Office: wo die jüngste Fassung im Archiv steht. */
  archiv: Archivvermerk | undefined;
};

/**
 * Eine ältere Fassung eines Papiers aus Machine Office. `id` steht im Pfad
 * des PDF — beim Angebot und bei der AB die der Fassung, beim Protokoll die
 * des Protokolls (dann wählt `fassung`).
 */
export type Belegfassung = {
  id: string;
  fassung: number;
  nummer: string;
  datum: string;
  archiv: Archivvermerk | undefined;
};

export type Belegtreffer = {
  belege: Beleg[];
  gesamt: number;
  gekappt: boolean;
  jeArt: Record<Belegart, number>;
  /** `false`: Lexware hatte mehr, als die Suche blättert — Zeitraum enger fassen. */
  vollstaendig: boolean;
  /** Wann die Liste drüben geholt wurde (ISO). */
  stand: string;
  /**
   * Lexware Office hat nicht geantwortet — der Fehlerschlüssel, sonst leer.
   * Die Papiere aus Machine Office stehen trotzdem da.
   */
  lexwareFehler: string;
};

export type Belegfrage = {
  /** Leer: alle vier. */
  arten: Belegart[];
  wort: string;
  kunde: string;
  von: string;
  bis: string;
  zustand: Belegzustand | "";
  hoechstens?: number;
};

/**
 * Die Frage, wie sie in der Adresse steht — `/belege?kunde=10008&art=rechnung`.
 *
 * Alles freiwillig. **Der Router liest `?kunde=10008` als Zahl** und
 * `?wort=12` ebenso; beim Lesen wird daraus wieder der Text, der es war —
 * dieselbe Falle wie bei der Vorbelegung des Angebots (`router.tsx`).
 *
 * **Und beim Schreiben ist die Kundennummer eine Zahl.** Als Text schreibt
 * der Router sie in Anführungszeichen (`?kunde="10037"`), damit er sie beim
 * Zurücklesen nicht für eine Zahl hält — gefunden am 18.09.2026. Eine
 * Kundennummer ist eine reine Ziffernfolge (Migration 0064), also geht sie
 * als Zahl hinein und kommt als Text heraus.
 */
export type Belegadresse = {
  wort?: string; kunde?: number; art?: string; von?: string; bis?: string; zustand?: string; beleg?: string;
  /**
   * **`alle` — jederzeit.** Ohne Angabe gilt das laufende Jahr (Luca,
   * 18.09.2026: *„soll immer das laufende Jahr vorausgewählt sein"*). Wer
   * ausdrücklich über alle Zeiten sucht, muss das deshalb sagen können —
   * sonst stünde nach dem Neuladen wieder dieses Jahr da.
   */
  zeitraum?: "alle";
};

/**
 * Länger wird ein Suchwort nicht — dieselbe Grenze wie `GET /belege` und die
 * Adresse. B-08 (03.10.2026): Wer 500 Zeichen einfügte, schickte erst den
 * ganzen Text hinaus und dann den gekürzten; jetzt kürzt schon das Feld.
 */
export const BELEGWORT_HOECHSTENS = 120;

export function belegwort(roh: string): string {
  return roh.slice(0, BELEGWORT_HOECHSTENS);
}

export function belegadresse(such: Record<string, unknown>): Belegadresse {
  const text = (wert: unknown): string =>
    typeof wert === "string" ? wert : typeof wert === "number" ? String(wert) : "";
  const tag = (wert: unknown): string => {
    const t = text(wert);
    return /^\d{4}-\d{2}-\d{2}$/.test(t) ? t : "";
  };
  const heraus: Belegadresse = {};
  const kundeRoh = text(such["kunde"]).trim().slice(0, 120);
  /* Ein Name statt einer Nummer (eine ältere Adresse, `?kunde=Kramer`) geht
     nicht verloren: Er wird zum Suchwort, das ihn genauso findet. */
  const wort = [text(such["wort"]), /^\d+$/.test(kundeRoh) ? "" : kundeRoh]
    .filter(Boolean).join(" ").slice(0, 120);
  const kunde = /^\d{1,12}$/.test(kundeRoh) ? Number(kundeRoh) : null;
  const art = text(such["art"]).split(",").filter((a) => (BELEGARTEN as readonly string[]).includes(a)).join(",");
  const zustand = text(such["zustand"]);
  const beleg = text(such["beleg"]);
  if (wort) heraus.wort = wort;
  if (kunde !== null) heraus.kunde = kunde;
  if (art) heraus.art = art;
  if (tag(such["von"])) heraus.von = tag(such["von"]);
  if (tag(such["bis"])) heraus.bis = tag(such["bis"]);
  if ((BELEGZUSTAENDE as readonly string[]).includes(zustand)) heraus.zustand = zustand;
  if (/^[0-9a-f-]{36}$/i.test(beleg)) heraus.beleg = beleg;
  if (such["zeitraum"] === "alle") heraus.zeitraum = "alle";
  return heraus;
}

function alsBeleg(r: Roh): Beleg | null {
  if (!istArt(r["art"])) return null;
  const kunde = (r["kunde"] ?? null) as Roh | null;
  const vorgang = (r["vorgang"] ?? null) as Roh | null;
  return {
    id: txt(r["id"]),
    art: r["art"],
    nummer: txt(r["nummer"]),
    zustand: alsZustand(r["zustand"]),
    status: txt(r["status"]),
    datum: txt(r["datum"]),
    faellig: txt(r["faellig"]),
    name: txt(r["kontakt_name"]),
    summe: zahlOderNull(r["summe"]),
    offen: zahlOderNull(r["offener_betrag"]),
    archiviert: r["archiviert"] === true,
    adresse: txt(r["adresse"]),
    kontaktAdresse: txt(r["kontakt_adresse"]),
    kundeNr: txt(kunde?.["nr"]),
    kundeName: txt(kunde?.["name"]),
    kundeOrt: txt(kunde?.["ort"]),
    vorgangNr: txt(vorgang?.["nr"]),
    vorgangStatus: txt(vorgang?.["status"]),
    ausMachineOffice: r["herkunft"] === "machine_office",
    fassung: zahlOderNull(r["fassung"]),
    fassungen: (Array.isArray(r["fassungen"]) ? (r["fassungen"] as Roh[]) : []).map((f) => ({
      id: txt(f["id"]),
      fassung: zahlOderNull(f["fassung"]) ?? 1,
      nummer: txt(f["nummer"]),
      datum: txt(f["datum"]),
      archiv: archivvermerkAus(f["archiv"]),
    })),
    archiv: archivvermerkAus(r["archiv"]),
  };
}

/** Leere Zähler — für jede Art eine Null. */
function zaehlerAus(je: Roh): Record<Belegart, number> {
  return Object.fromEntries(BELEGARTEN.map((a) => [a, zahlOderNull(je[a]) ?? 0])) as Record<Belegart, number>;
}

function frageAlsAnhang(frage: Belegfrage, frisch: boolean): string {
  const s = new URLSearchParams();
  if (frage.arten.length) s.set("arten", frage.arten.join(","));
  if (frage.wort.trim()) s.set("wort", frage.wort.trim());
  if (frage.kunde.trim()) s.set("kunde", frage.kunde.trim());
  if (frage.von) s.set("von", frage.von);
  if (frage.bis) s.set("bis", frage.bis);
  if (frage.zustand) s.set("zustand", frage.zustand);
  if (frage.hoechstens) s.set("hoechstens", String(frage.hoechstens));
  if (frisch) s.set("frisch", "1");
  return s.toString();
}

export async function belegeSuchen(frage: Belegfrage, frisch = false): Promise<Belegtreffer> {
  if (ATTRAPPE) {
    return {
      belege: [], gesamt: 0, gekappt: false, vollstaendig: true, stand: new Date().toISOString(),
      jeArt: zaehlerAus({}), lexwareFehler: "",
    };
  }
  const roh = await hole<Roh>(`/belege?${frageAlsAnhang(frage, frisch)}`);
  const je = (roh["je_art"] ?? {}) as Roh;
  const belege = (Array.isArray(roh["belege"]) ? (roh["belege"] as Roh[]) : [])
    .map(alsBeleg)
    .filter((b): b is Beleg => b !== null);
  return {
    belege,
    gesamt: zahlOderNull(roh["gesamt"]) ?? belege.length,
    gekappt: roh["gekappt"] === true,
    vollstaendig: roh["vollstaendig"] !== false,
    stand: txt(roh["stand"]),
    jeArt: zaehlerAus(je),
    lexwareFehler: txt(roh["lexware_fehler"]),
  };
}

/**
 * Die Belege zu einer Frage.
 *
 * **Die letzte Antwort bleibt stehen, solange die neue unterwegs ist**
 * (`placeholderData`) — sonst springt die Liste bei jedem Buchstaben ins
 * Leere. Dieselbe Überlegung wie beim Nachschlagen.
 *
 * `staleTime` von einer Minute: So lange hält auch der Server die Liste.
 * Ein Zurück in die Maske fragt dann nicht noch einmal, was eben schon da
 * war.
 */
export function useBelege(frage: Belegfrage, an = true) {
  return useQuery({
    queryKey: [
      "belege", frage.arten.join(","), frage.wort.trim(), frage.kunde.trim(),
      frage.von, frage.bis, frage.zustand, frage.hoechstens ?? 0,
    ] as const,
    queryFn: () => belegeSuchen(frage),
    enabled: an,
    staleTime: 60_000,
    placeholderData: (vorher: Belegtreffer | undefined) => vorher,
  });
}

/** Ist das ein Protokoll? Dann wählt die Fassung eine Zahl, nicht eine eigene Kennung. */
export function istProtokoll(art: Belegart): boolean {
  return art === "uebergabeprotokoll" || art === "ruecknahmeprotokoll";
}

/**
 * Der Weg zum PDF eines Belegs — `GET /belege/:art/:id/pdf`. Beim Protokoll
 * mit `?fassung=N`, wenn eine ältere gemeint ist; sonst die jüngste.
 */
export function belegWeg(art: Belegart, id: string, fassung?: number | null): string {
  const anhang = istProtokoll(art) && fassung ? `?fassung=${fassung}` : "";
  return `/belege/${art}/${encodeURIComponent(id)}/pdf${anhang}`;
}

/**
 * **Einen Beleg öffnen** (06.10.2026) — im eigenen Reiter, am Telefon im
 * Betrachter des Systems; nicht mehr herunterladen (`papieroeffnen.ts`).
 * Das Kontext-Token geht nur als Kopf mit, ein nackter Link bekäme 401.
 */
export function useBelegOeffnen() {
  return useMutation({
    mutationFn: (b: { art: Belegart; id: string; nummer?: string; fassung?: number | null }) =>
      papierOeffnen(async () => {
        const datei = await holeDatei(belegWeg(b.art, b.id, b.fassung));
        return { blob: datei.blob, name: datei.name || `${b.nummer || b.art}.pdf` };
      }),
  });
}

/** Wohin der Vorgang eines Belegs führt: ein Angebot oder ein Auftrag. */
export function vorgangsweg(status: string): "/angebote/$nr" | "/auftraege/$nr" {
  return status === "entwurf" || status === "angebot" || status === "abgelehnt"
    ? "/angebote/$nr"
    : "/auftraege/$nr";
}

/**
 * Die Tönung der Zustandsmarke.
 *
 * **„Offen" ist nur an der Rechnung eine Ampel.** Eine offene Rechnung
 * wartet auf Geld; eine ausgestellte Auftragsbestätigung ist der Normalfall
 * und bekommt keine Farbe — eine Liste, in der alles gelb ist, sagt nichts
 * mehr.
 */
export function zustandsart(b: Pick<Beleg, "art" | "zustand">): "" | "gut" | "acht" | "stopp" {
  if (b.zustand === "bezahlt" || b.zustand === "angenommen") return "gut";
  if (b.zustand === "offen" && b.art === "rechnung") return "acht";
  if (b.zustand === "ueberfaellig" || b.zustand === "abgelehnt") return "stopp";
  return "";
}
